import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, DollarSign, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WOCostsModal = ({ onClose, wonum }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const currency = session.currency || 'EGP';

  useEffect(() => {
    if (!wonum) return;
    const fetchCosts = async () => {
      try {
        const res = await axios.get(`/api/workorder/${encodeURIComponent(wonum)}/costs`);
        setCosts(res.data);
      } catch (err) {
        console.error("Failed to fetch costs", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCosts();
  }, [wonum]);

  // دالة أمان لعرض الأرقام
  const formatNum = (num) => Number(num || 0).toLocaleString();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-surface-card w-full max-w-2xl rounded-lg shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 ${isRTL ? 'text-right' : 'text-left'}`}>

        {/* Header */}
        <div className={`bg-primary-dark p-4 flex justify-between items-center text-ink-inverse `}>
          <div className={`flex items-center gap-3 `}>
            <DollarSign className="text-status-success" />
            <h2 className="text-sm font-black uppercase tracking-widest">
              {t('wo.costs.title')} - {wonum}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-surface-panel flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 opacity-50">
              <Activity className="animate-spin mb-2" size={32} />
              <span className="text-xs font-black uppercase tracking-widest">{t('wo.costs.calculating')}</span>
            </div>
          ) : costs ? (
            <div className="space-y-6">
              
              <div className="grid grid-cols-2 gap-6">
                {/* Estimated Box */}
                <div className="bg-surface-card p-4 border border-primary-soft rounded-lg shadow-sm">
                  <h3 className="text-xs font-black text-primary uppercase border-b border-line-subtle pb-2 mb-3">{t('wo.costs.estimated')}</h3>
                  <div className="space-y-2 text-sm font-bold text-ink-secondary">
                    <div className={`flex justify-between `}><span>{t('wo.costs.labor')}:</span> <span>{formatNum(costs.estimated.labor)} <small className="opacity-50">{currency}</small></span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.materials')}:</span> <span>{formatNum(costs.estimated.materials)} <small className="opacity-50">{currency}</small></span></div>
                  </div>
                  <div className={`mt-4 pt-2 border-t border-line-subtle flex justify-between font-black text-primary text-lg `}>
                    <span>{t('wo.costs.total')}:</span> <span>{formatNum(costs.estimated.total)} <small className="text-xs opacity-50">{currency}</small></span>
                  </div>
                </div>

                {/* Actual Box */}
                <div className="bg-surface-card p-4 border border-status-success-bg rounded-lg shadow-sm">
                  <h3 className="text-xs font-black text-status-success uppercase border-b border-line-subtle pb-2 mb-3">{t('wo.costs.actual')}</h3>
                  <div className="space-y-2 text-sm font-bold text-ink-secondary">
                    <div className={`flex justify-between `}><span>{t('wo.costs.labor')}:</span> <span>{formatNum(costs.actual.labor)} <small className="opacity-50">{currency}</small></span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.materials')}:</span> <span>{formatNum(costs.actual.materials)} <small className="opacity-50">{currency}</small></span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.tools')}:</span> <span>{formatNum(costs.actual.tools)} <small className="opacity-50">{currency}</small></span></div>
                    <div className={`flex justify-between `}><span>{t('wo.costs.services')}:</span> <span>{formatNum(costs.actual.services)} <small className="opacity-50">{currency}</small></span></div>
                  </div>
                  <div className={`mt-4 pt-2 border-t border-line-subtle flex justify-between font-black text-status-success text-lg `}>
                    <span>{t('wo.costs.total')}:</span> <span>{formatNum(costs.actual.total)} <small className="text-xs opacity-50">{currency}</small></span>
                  </div>
                </div>
              </div>

              {/* Variance Alert */}
              <div className={`p-4 rounded-lg flex items-center justify-between border-l-4 ${isRTL ? 'border-r-4 border-l-0' : ''} ${costs.variance > 0 ? 'bg-status-danger-bg border-status-danger' : 'bg-status-success-bg border-status-success'} `}>
                <div className={`flex items-center gap-3 `}>
                  {costs.variance > 0 ? <TrendingUp className="text-status-danger" /> : <TrendingDown className="text-status-success" />}
                  <span className="font-black text-sm uppercase">{t('wo.costs.variance')}</span>
                </div>
                <span className={`text-xl font-black ${costs.variance > 0 ? 'text-status-danger' : 'text-status-success'}`}>
                  {costs.variance > 0 ? '+' : ''}{formatNum(costs.variance)} <small className="text-xs opacity-50">{currency}</small>
                </span>
              </div>

            </div>
          ) : (
            <div className="text-center py-10 text-ink-secondary font-bold italic">{t('wo.costs.no_data')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WOCostsModal;